import { useState, useEffect } from 'react';
import { FileText, Download, Calendar, AlertCircle, RefreshCw } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { usePackAccess } from '../hooks/usePackAccess';

interface FinancialReport {
  id: string;
  user_id: string;
  month: string;
  report_type: string;
  data: any;
  created_at: string;
}

export function RapportsTab() {
  const { canAccessReports, currentPlan } = usePackAccess();
  const [reports, setReports] = useState<FinancialReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    if (canAccessReports) {
      loadReports();
    } else {
      setLoading(false);
    }
  }, [canAccessReports]);

  const loadReports = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('financial_reports')
        .select('*')
        .eq('user_id', user.id)
        .order('month', { ascending: false });

      if (error) throw error;
      if (data) setReports(data);
    } catch (error) {
      console.error('Error loading reports:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateReport = async () => {
    setGenerating(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-financial-report`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ userId: user.id })
        }
      );

      if (!response.ok) throw new Error('Failed to generate report');

      await loadReports();
    } catch (error) {
      console.error('Error generating report:', error);
      alert('Erreur lors de la génération du rapport');
    } finally {
      setGenerating(false);
    }
  };

  const formatMonth = (month: string) => {
    const [year, monthNum] = month.split('-');
    const date = new Date(parseInt(year), parseInt(monthNum) - 1);
    return date.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
  };

  const getReportTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      monthly: 'Rapport mensuel',
      quarterly: 'Rapport trimestriel',
      annual: 'Rapport annuel'
    };
    return labels[type] || type;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-gray-600">Chargement...</div>
      </div>
    );
  }

  if (!canAccessReports) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-8">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-orange-500 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            Rapports financiers non disponibles
          </h3>
          <p className="text-gray-600 mb-6">
            Les rapports financiers automatisés sont disponibles à partir du Pack Pilotage.
            Passez à un pack supérieur pour accéder à cette fonctionnalité.
          </p>
          <p className="text-sm text-gray-500">
            Pack actuel : <span className="font-semibold">{currentPlan?.name || 'Aucun'}</span>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Rapports financiers</h2>
          <p className="text-gray-600 mt-1">
            Consultez l'historique de vos rapports mensuels automatisés
          </p>
        </div>

        <button
          onClick={handleGenerateReport}
          disabled={generating}
          className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <RefreshCw className={`h-5 w-5 ${generating ? 'animate-spin' : ''}`} />
          <span>{generating ? 'Génération...' : 'Générer un rapport'}</span>
        </button>
      </div>

      <div className="bg-blue-50 border-l-4 border-blue-600 p-4 rounded">
        <div className="flex items-start">
          <FileText className="h-5 w-5 text-blue-600 mt-0.5 mr-3" />
          <div>
            <p className="text-sm text-blue-800">
              <span className="font-semibold">Rapports automatiques</span> - Un rapport est généré automatiquement chaque mois.
              Vous pouvez également générer un rapport à la demande à tout moment.
            </p>
          </div>
        </div>
      </div>

      {reports.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm p-8 text-center">
          <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">Aucun rapport disponible pour le moment</p>
          <p className="text-sm text-gray-500 mt-2">
            Générez votre premier rapport ou attendez la génération automatique mensuelle
          </p>
        </div>
      ) : (
        <div className="grid gap-4">
          {reports.map((report) => (
            <div
              key={report.id}
              className="bg-white rounded-lg shadow-sm p-6 border border-gray-200 hover:border-gray-300 transition-colors"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="p-3 bg-blue-50 rounded-lg">
                    <FileText className="h-6 w-6 text-blue-600" />
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      {getReportTypeLabel(report.report_type)}
                    </h3>
                    <div className="flex items-center space-x-4 mt-1 text-sm text-gray-600">
                      <span className="flex items-center">
                        <Calendar className="h-4 w-4 mr-1" />
                        {formatMonth(report.month)}
                      </span>
                      <span>
                        Généré le {new Date(report.created_at).toLocaleDateString('fr-FR')}
                      </span>
                    </div>
                  </div>
                </div>

                <button
                  onClick={() => {
                    const blob = new Blob([JSON.stringify(report.data, null, 2)], { type: 'application/json' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `rapport-${report.month}.json`;
                    a.click();
                    URL.revokeObjectURL(url);
                  }}
                  className="flex items-center space-x-2 bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  <Download className="h-5 w-5" />
                  <span>Télécharger</span>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
